import { create } from 'zustand';

const CALL_STATES = Object.freeze({
  IDLE: 'idle',
  ACQUIRING: 'acquiring',
  WAITING: 'waiting',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ENDED: 'ended',
});

/**
 * @typedef {object} Participant
 * @property {string} userId
 * @property {'operator'|'client'} role
 */

/**
 * @typedef {object} Consumer
 * @property {string} id
 * @property {string} producerId
 * @property {'audio'|'video'} kind
 * @property {any} rtpParameters
 * @property {boolean} paused
 */

const initialState = {
  room: /** @type {null|{id: string; status: string}} */ (null),
  role: /** @type {'operator'|'client'|null} */ (null),
  state: CALL_STATES.IDLE,
  localStream: /** @type {MediaStream|null} */ (null),
  deviceReady: false,
  remoteStreams: /** @type {Map<string, MediaStream>} */ (new Map()),
  participants: /** @type {Map<string, Participant>} */ (new Map()),
  producers: /** @type {Producer[]} */ ([]),
  consumers: /** @type {Consumer[]} */ ([]),
  error: /** @type {string|null} */ (null),
};

export const useCallStore = create(
  /** @param {import('zustand').SetStateFn<typeof initialState>} set */
  (set) => ({
    ...initialState,

    startAcquiring: (room) =>
      set({
        room,
        state: CALL_STATES.ACQUIRING,
        error: null,
      }),

    setWaiting: () =>
      set({
        state: CALL_STATES.WAITING,
        error: null,
      }),

    setConnecting: () =>
      set({
        state: CALL_STATES.CONNECTING,
        error: null,
      }),

    setConnected: (localStream) =>
      set({
        state: CALL_STATES.CONNECTED,
        localStream,
        error: null,
      }),

    setDeviceReady: (ready) =>
      set({ deviceReady: ready }),

    setRole: (role) =>
      set({ role }),

    addRemoteStream: (userId, stream) =>
      set((state) => {
        const streams = new Map(state.remoteStreams);
        streams.set(userId, stream);
        return { remoteStreams: streams };
      }),

    removeRemoteStream: (userId) =>
      set((state) => {
        const streams = new Map(state.remoteStreams);
        streams.delete(userId);
        return { remoteStreams: streams };
      }),

    setParticipant: (userId, role) =>
      set((state) => {
        const participants = new Map(state.participants);
        participants.set(userId, { userId, role });
        return { participants };
      }),

    removeParticipant: (userId) =>
      set((state) => {
        const participants = new Map(state.participants);
        participants.delete(userId);
        return { participants };
      }),

    addProducer: (id, kind, remoteUserId) =>
      set((state) => ({
        producers: [
          ...state.producers,
          { id, kind, remoteUserId },
        ],
      })),

    removeProducer: (id) =>
      set((state) => ({
        producers: state.producers.filter((p) => p.id !== id),
      })),

    addConsumer: (consumer) =>
      set((state) => ({
        consumers: [...state.consumers, consumer],
      })),

    setConsumerPaused: (id, paused) =>
      set((state) => ({
        consumers: state.consumers.map((c) =>
          c.id === id ? { ...c, paused } : c,
        ),
      })),

    setError: (error) =>
      set({ error }),

    endCall: () =>
      set((state) => ({
        state: CALL_STATES.ENDED,
        localStream: null,
        remoteStreams: new Map(),
        producers: [],
        consumers: [],
        role: null,
      })),

    reset: () =>
      set({ ...initialState }),
  }),
);

export { CALL_STATES };
